// Schéma FERMÉ des mises en page PDF (spec 2026-07-21-pdf-layouts-editables §3).
// Module PUR (aucun import). ⚠️ validateLayoutTokens et les constantes d'enum sont
// DUPLIQUÉES dans supabase/functions/modify-pdf-layout/index.ts (EF autonome,
// bundler MCP sans ../_shared/) — garder identiques.

export interface PdfLayoutTokens {
  header: {
    variant: 'left' | 'centered' | 'band' | 'letterhead' | 'rail' | 'contemporary';
    showLogo: boolean;
    firmFont: 'heading' | 'body';
    firmSize: number;
    firmColor: 'primary' | 'ink' | 'white';
    firmUppercase: boolean;
    subtitle: string | null;
    coordsText: string | null;
    rule: 'none' | 'gold' | 'dark';
  };
  title: {
    font: 'heading' | 'body';
    color: 'primary' | 'ink';
    uppercase: boolean;
    underline: 'none' | 'gold' | 'ink';
    align: 'left' | 'center';
    accentBar: boolean;
  };
  body: { justify: boolean; fontSize: number };
  footer: {
    text: string | null;
    rule: 'none' | 'gold' | 'dark' | 'light';
    showPagination: boolean;
    align: 'between' | 'center';
    band: boolean;
  };
  rail: { enabled: boolean; showFirmVertical: boolean };
}

export const DEFAULT_LAYOUT_TOKENS: PdfLayoutTokens = {
  header: {
    variant: 'rail', showLogo: true, firmFont: 'heading', firmSize: 15,
    firmColor: 'primary', firmUppercase: false, subtitle: null,
    coordsText: 'Adresse du cabinet — Téléphone', rule: 'none',
  },
  title: { font: 'heading', color: 'primary', uppercase: false, underline: 'none', align: 'left', accentBar: false },
  body: { justify: true, fontSize: 10.5 },
  footer: { text: null, rule: 'none', showPagination: true, align: 'between', band: false },
  rail: { enabled: true, showFirmVertical: true },
};

const ENUMS: Record<string, readonly string[]> = {
  'header.variant': ['left', 'centered', 'band', 'letterhead', 'rail', 'contemporary'],
  'header.firmFont': ['heading', 'body'],
  'header.firmColor': ['primary', 'ink', 'white'],
  'header.rule': ['none', 'gold', 'dark'],
  'title.font': ['heading', 'body'],
  'title.color': ['primary', 'ink'],
  'title.underline': ['none', 'gold', 'ink'],
  'title.align': ['left', 'center'],
  'footer.rule': ['none', 'gold', 'dark', 'light'],
  'footer.align': ['between', 'center'],
};
const MAX_TEXT = 300;

type Ctx = { errors: string[] };
function pickEnum(obj: Record<string, unknown>, section: string, key: string, ctx: Ctx): string {
  const path = `${section}.${key}`;
  const val = obj[key];
  if (typeof val === 'string' && ENUMS[path]!.includes(val)) return val;
  ctx.errors.push(`${path} invalide`);
  return ENUMS[path]![0]!;
}
function pickBool(obj: Record<string, unknown>, section: string, key: string, ctx: Ctx): boolean {
  const val = obj[key];
  if (typeof val === 'boolean') return val;
  ctx.errors.push(`${section}.${key} doit être un booléen`);
  return false;
}
function pickNumber(obj: Record<string, unknown>, section: string, key: string, min: number, max: number, ctx: Ctx): number {
  const val = obj[key];
  if (typeof val === 'number' && Number.isFinite(val)) return Math.min(max, Math.max(min, val));
  ctx.errors.push(`${section}.${key} doit être un nombre`);
  return min;
}
function pickText(obj: Record<string, unknown>, section: string, key: string, ctx: Ctx): string | null {
  const val = obj[key];
  if (val === null || val === undefined) return null;
  if (typeof val === 'string') return val.slice(0, MAX_TEXT);
  ctx.errors.push(`${section}.${key} doit être un texte ou null`);
  return null;
}
function section(value: Record<string, unknown>, key: string, ctx: Ctx): Record<string, unknown> | null {
  const s = value[key];
  if (s && typeof s === 'object' && !Array.isArray(s)) return s as Record<string, unknown>;
  ctx.errors.push(`section ${key} manquante`);
  return null;
}

export function validateLayoutTokens(
  value: unknown,
): { ok: true; tokens: PdfLayoutTokens } | { ok: false; errors: string[] } {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return { ok: false, errors: ['tokens doit être un objet'] };
  }
  const v = value as Record<string, unknown>;
  const ctx: Ctx = { errors: [] };
  const h = section(v, 'header', ctx);
  const t = section(v, 'title', ctx);
  const b = section(v, 'body', ctx);
  const f = section(v, 'footer', ctx);
  const r = section(v, 'rail', ctx);
  if (!h || !t || !b || !f || !r) return { ok: false, errors: ctx.errors };

  // Les clés inconnues sont simplement ignorées : on ne reconstruit QUE le schéma.
  const tokens: PdfLayoutTokens = {
    header: {
      variant: pickEnum(h, 'header', 'variant', ctx) as PdfLayoutTokens['header']['variant'],
      showLogo: pickBool(h, 'header', 'showLogo', ctx),
      firmFont: pickEnum(h, 'header', 'firmFont', ctx) as PdfLayoutTokens['header']['firmFont'],
      firmSize: pickNumber(h, 'header', 'firmSize', 11, 24, ctx),
      firmColor: pickEnum(h, 'header', 'firmColor', ctx) as PdfLayoutTokens['header']['firmColor'],
      firmUppercase: pickBool(h, 'header', 'firmUppercase', ctx),
      subtitle: pickText(h, 'header', 'subtitle', ctx),
      coordsText: pickText(h, 'header', 'coordsText', ctx),
      rule: pickEnum(h, 'header', 'rule', ctx) as PdfLayoutTokens['header']['rule'],
    },
    title: {
      font: pickEnum(t, 'title', 'font', ctx) as PdfLayoutTokens['title']['font'],
      color: pickEnum(t, 'title', 'color', ctx) as PdfLayoutTokens['title']['color'],
      uppercase: pickBool(t, 'title', 'uppercase', ctx),
      underline: pickEnum(t, 'title', 'underline', ctx) as PdfLayoutTokens['title']['underline'],
      align: pickEnum(t, 'title', 'align', ctx) as PdfLayoutTokens['title']['align'],
      accentBar: pickBool(t, 'title', 'accentBar', ctx),
    },
    body: {
      justify: pickBool(b, 'body', 'justify', ctx),
      fontSize: pickNumber(b, 'body', 'fontSize', 9, 12, ctx),
    },
    footer: {
      text: pickText(f, 'footer', 'text', ctx),
      rule: pickEnum(f, 'footer', 'rule', ctx) as PdfLayoutTokens['footer']['rule'],
      showPagination: pickBool(f, 'footer', 'showPagination', ctx),
      align: pickEnum(f, 'footer', 'align', ctx) as PdfLayoutTokens['footer']['align'],
      band: pickBool(f, 'footer', 'band', ctx),
    },
    rail: {
      enabled: pickBool(r, 'rail', 'enabled', ctx),
      showFirmVertical: pickBool(r, 'rail', 'showFirmVertical', ctx),
    },
  };
  if (ctx.errors.length > 0) return { ok: false, errors: ctx.errors };
  return { ok: true, tokens };
}
